/**
 * <equilibrium-buybox> — custom element for snippets/equilibrium-buybox.liquid (the
 * buy box itself; sections/equilibrium-buybox.liquid is now a thin pass-through to
 * that snippet, kept only for a possible future full-width placement), built for
 * Sentro Labs' live theme, Concept 5.3.1 (theme_store_id 2412). NOT Dawn — see the
 * snippet's header comment for how that was confirmed and what changed in the
 * 2026-09-10 fix passes.
 *
 * What it does, and only this:
 *   1. Keeps the visual "selected card" state (.eq-option--selected) in sync with
 *      whichever radio in the purchase_option group is actually checked — click OR
 *      keyboard/arrow-key navigation within the native radiogroup.
 *   2. Writes the checked radio's data-variant-id / data-selling-plan-id onto the
 *      form's real `id` and `selling_plan` hidden inputs before submit.
 *
 * What it deliberately does NOT do:
 *   - It does not intercept form submission or call the Cart API itself. The form is a
 *     real `{% form 'product' %}` rendered with `is="product-form"` — Concept's own
 *     customized built-in (`class ProductForm extends HTMLFormElement`, registered as
 *     `customElements.define("product-form", ProductForm, {extends: "form"})`), which
 *     already owns the AJAX add-to-cart, loading state, and cart-drawer behaviour.
 *     CONFIRMED by reading Concept's theme.js directly: `onSubmitHandler` reads
 *     `new FormData(this)` (so `id`, `quantity` and `selling_plan` all travel through
 *     unmodified), fetches `theme.routes.cart_add_url`, and on a successful response
 *     calls `this.cartDrawer?.show(this.activeElement)`, where `cartDrawer` is
 *     `document.querySelector("cart-drawer")`. This element only makes sure the
 *     fields the native ProductForm reads are correct at the moment of submit — it
 *     never touches fetch, the cart drawer, or Concept's submit-button state itself.
 *   - It does not call Appstle's JS API. Per the 2026-08-28 research (Deliverables/
 *     2026-08-28 Sentro Labs Appstle subscription buy box research and recommendation.md
 *     §2), keeping the add-to-cart inside a standard product form — rather than a bare
 *     fetch call — is what lets Appstle's own widget JS still recognise the subscription
 *     for its customer portal, analytics and retention tooling. Nothing here bypasses
 *     that form.
 *
 * QA note for Arete, updated 2026-09-10: the prior version of this note flagged the
 * submit-then-fetch path as unconfirmed and needing a live Appstle-account test. That
 * specific seam is now CONFIRMED by reading Concept's ProductForm class body end to
 * end (submit → preventDefault → fetch(cart_add_url) → cartDrawer.show()) — this is
 * not a live-store observation, it is a static read of the theme's own shipped JS,
 * so it tells you what Concept's code does, not what actually happens when this
 * section's markup is rendered inside it. What is still UNCONFIRMED and still needs a
 * live test: (1) that Appstle's own widget JS, once mounted elsewhere on the same
 * page, does not also react to this section's separate form's submit event or its
 * `selling_plan` field in some way this read didn't surface; (2) whether Appstle
 * mounts a competing subscription-choice UI on this template at all — see the
 * PLACEMENT note in the section file's header comment, which is a genuine gap in
 * what the saved files could show.
 */
(function () {
  if (customElements.get('equilibrium-buybox')) return;

  class EquilibriumBuybox extends HTMLElement {
    connectedCallback() {
      this.radios = Array.from(this.querySelectorAll('input[name="eq_purchase_option"]'));
      this.variantInput = this.querySelector('[data-eq-variant-id-target]');
      this.sellingPlanInput = this.querySelector('[data-eq-selling-plan-target]');
      this.quantityInput = this.querySelector('[data-eq-quantity-target]');

      this.onChange = this.sync.bind(this);
      this.radios.forEach(function (radio) {
        radio.addEventListener('change', this.onChange);
      }, this);

      // Also resolve the fields once at connect, in case the checked radio at load
      // (server-rendered `checked` attribute) never fires a `change` event.
      this.sync();
    }

    disconnectedCallback() {
      this.radios.forEach(function (radio) {
        radio.removeEventListener('change', this.onChange);
      }, this);
    }

    sync() {
      var checked = this.radios.find(function (radio) {
        return radio.checked;
      });
      if (!checked) return;

      if (this.variantInput) {
        this.variantInput.value = checked.dataset.variantId || '';
      }
      if (this.sellingPlanInput) {
        // Empty string, not omitted — Shopify's /cart/add.js treats a blank
        // selling_plan the same as no selling_plan (one-time purchase).
        this.sellingPlanInput.value = checked.dataset.sellingPlanId || '';
      }
      if (this.quantityInput) {
        // Subscription adds 3 (the 90-day plan), one-time adds 1 (the 30-day
        // supply) — data-quantity is rendered per-radio from the same
        // subscription_quantity / one_time_quantity params that drive the
        // displayed prices, so this never drifts from what's on screen.
        this.quantityInput.value = checked.dataset.quantity || '1';
      }

      this.radios.forEach(function (radio) {
        var card = radio.closest('.eq-option');
        if (card) {
          card.classList.toggle('eq-option--selected', radio.checked);
        }
      });
    }
  }

  customElements.define('equilibrium-buybox', EquilibriumBuybox);
})();
